"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { Carousel } from "@nxt-costco-com/forge-components";
import { SpaceMd, FontLineHeightMd, FontWeightBold, BpXl, SpaceLg } from "@nxt-costco-com/forge-design-tokens";
import { AdSetTitle } from "../../AdSet/AdSetTitle.js";
import { AD_TYPE_HALF, AD_TYPE_FULL } from "../AdButler/constants.js";
import { AdSetContainerStyles } from "../styles.js";
import { CostcoBox } from "../../../Util/CostcoBox.js";
import { useCheckScreen } from "../../../../hooks/useCheckScreen/index.js";
import { Observable } from "./Observable.js";
import { ProductTileCarouselSkeleton } from "./ProductTileCarouselSkeleton.js";
import { ProductTiles } from "./ProductTiles.js";
import { SKELETON_COMPONENT_NAME, COMPONENT_NAME } from "./constants.js";
import { CarouselContainer, ProductTileContainerSx } from "./styles.js";
function ProductTileCarouselUI({
  analytics = {},
  containerSx = {},
  itemNumbers,
  productData,
  isLoadingProductData,
  productDataLoadingError,
  locale,
  variant,
  inventoryAware,
  showReviews,
  showActionButton,
  testId,
  isHorizontal,
  showSlideNumber,
  criteoDataSourceAttr,
  maxSlides,
  maxSlidesBySize,
  titleProps,
  navigationButtonBottomOffset = "auto",
  disableBottomControls,
  minProductTileImageWidth,
  maxProductTileImageWidth,
  productTileMaxLineNumber,
  hideReviewStarValue,
  maxProductTileImageHeight,
  productTileSx = {},
  productTileContainerSx = {},
  sx = {},
  peekAdjacent,
  wrapAround,
  ariaLabel = "",
  productTileNumberSeparator,
  slideGapSizes,
  navigationButtonsBottomAlign,
  navigationButtonsMobileDisplay,
  displayMultipleTiles,
  pageType,
  maxItemsPerSlide,
  componentType,
  randomizeItems,
  isProductDetailsPage,
  dataPrivate,
  isCriteo,
  product_tile_data_source
}) {
  var _a, _b, _c, _d, _e, _f;
  const maxHeight = containerSx == null ? void 0 : containerSx.maxHeight;
  const { isDesktop, isTablet, isMobile } = useCheckScreen();
  let navigationButtonTopOffset = "110px";
  if (isDesktop) {
    maxItemsPerSlide = (maxSlidesBySize == null ? void 0 : maxSlidesBySize.desktop) || 5;
  } else if (isTablet) {
    if (componentType === AD_TYPE_HALF) {
      maxItemsPerSlide = 2;
    } else {
      maxItemsPerSlide = (maxSlidesBySize == null ? void 0 : maxSlidesBySize.tablet) || 3;
      navigationButtonTopOffset = "115px";
    }
  } else if (isMobile) {
    if (componentType === AD_TYPE_HALF) {
      maxItemsPerSlide = 1;
    } else {
      maxItemsPerSlide = (maxSlidesBySize == null ? void 0 : maxSlidesBySize.mobile) || 2;
    }
  }
  let slideGap = (slideGapSizes == null ? void 0 : slideGapSizes.desktop) || SpaceMd;
  if (isTablet && (slideGapSizes == null ? void 0 : slideGapSizes.tablet)) {
    slideGap = slideGapSizes == null ? void 0 : slideGapSizes.tablet;
  } else if (isMobile && (slideGapSizes == null ? void 0 : slideGapSizes.mobile)) {
    slideGap = slideGapSizes == null ? void 0 : slideGapSizes.mobile;
  }
  if (maxSlides) {
    maxItemsPerSlide = maxSlides;
  }
  let carouselItems;
  let isLWResponseData = false;
  if ((_a = productData == null ? void 0 : productData.productApiResponses) == null ? void 0 : _a.length) {
    if (((_d = (_c = (_b = productData == null ? void 0 : productData.configData) == null ? void 0 : _b.featureToggleConfig) == null ? void 0 : _c.callProductSummaryAPI) == null ? void 0 : _d.isEnabled) || product_tile_data_source === "product_summary_api") {
      carouselItems = ((_e = productData == null ? void 0 : productData.productApiResponses) == null ? void 0 : _e.filter((res) => {
        var _a2, _b2;
        const data = ((_b2 = (_a2 = res == null ? void 0 : res.productApiResponseData) == null ? void 0 : _a2.productData) == null ? void 0 : _b2[0]) || {};
        return !!(data == null ? void 0 : data.isPublished);
      })) || itemNumbers;
    } else {
      carouselItems = ((_f = productData == null ? void 0 : productData.productApiResponses) == null ? void 0 : _f.filter((res) => {
        var _a2, _b2, _c2, _d2, _e2, _f2;
        const data = ((_a2 = res == null ? void 0 : res.productApiResponseData) == null ? void 0 : _a2.data) || {};
        return !!((_c2 = (_b2 = data == null ? void 0 : data.products) == null ? void 0 : _b2.catalogData) == null ? void 0 : _c2.length) && !!((_f2 = (_e2 = (_d2 = data == null ? void 0 : data.products) == null ? void 0 : _d2.catalogData) == null ? void 0 : _e2[0]) == null ? void 0 : _f2.published);
      })) || itemNumbers;
    }
  } else if (productData == null ? void 0 : productData.isLucidWorksData) {
    carouselItems = (productData == null ? void 0 : productData.lwResponses) || [];
    isLWResponseData = true;
  } else {
    carouselItems = itemNumbers;
  }
  let maxWidth;
  if (maxItemsPerSlide && maxItemsPerSlide > 1 && (carouselItems == null ? void 0 : carouselItems.length) < maxItemsPerSlide) {
    if (isDesktop) {
      maxWidth = `calc(${BpXl}/${maxItemsPerSlide})`;
    } else {
      maxWidth = `calc(calc(100vw - ${SpaceLg})/${maxItemsPerSlide})`;
    }
  }
  const finalProductTileSx = {
    height: "100%",
    justifyContent: isHorizontal ? "center" : "space-between",
    marginTop: isHorizontal ? 0 : 1,
    maxHeight,
    maxWidth,
    paddingTop: isHorizontal ? 1 : 0,
    ...ProductTileContainerSx,
    ...productTileSx
  };
  if (!isLoadingProductData && (!productData || carouselItems.length === 0)) {
    if (productDataLoadingError) {
      console.error(productDataLoadingError);
    }
    return null;
  }
  const carouselContainerSx = {
    ...CarouselContainer,
    ...containerSx
  };
  const adSetContainerStyles = AdSetContainerStyles(
    componentType ?? AD_TYPE_FULL
  );
  if (isLoadingProductData) {
    return /* @__PURE__ */ jsx(
      CostcoBox,
      {
        "data-testid": `${testId}_${SKELETON_COMPONENT_NAME}`,
        sx: { ...adSetContainerStyles, ...sx },
        children: /* @__PURE__ */ jsx(
          ProductTileCarouselSkeleton,
          {
            containerSx: {
              ...finalProductTileSx,
              justifyContent: "center"
            },
            carouselContainerSx,
            expectedProductNumber: maxItemsPerSlide,
            isHorizontal,
            maxItemsPerSlide,
            minProductTileImageWidth,
            titleProps
          }
        )
      }
    );
  }
  const itemsPerSlide = Math.min(maxItemsPerSlide, carouselItems.length);
  if (carouselItems.length < maxItemsPerSlide) {
    carouselContainerSx.maxWidth = `${100 / maxItemsPerSlide * carouselItems.length}%`;
  }
  const carouselTitle = titleProps == null ? void 0 : titleProps.title;
  return /* @__PURE__ */ jsx(
    Observable,
    {
      observer: {
        con: true,
        minimumViewTime: analytics.minimumViewTime,
        onLoad: () => {
          analytics.onCarouselRendered && analytics.onCarouselRendered();
        },
        onView: analytics.onCarouselViewed,
        viewThreshold: analytics.viewThreshold
      },
      children: /* @__PURE__ */ jsxs(
        CostcoBox,
        {
          sx: {
            ...adSetContainerStyles,
            ...sx
          },
          "data-testid": `${testId}_${COMPONENT_NAME}`,
          children: [
            titleProps && /* @__PURE__ */ jsx(
              AdSetTitle,
              {
                titleSx: isProductDetailsPage ? {
                  fontWeight: FontWeightBold,
                  lineHeight: FontLineHeightMd
                } : void 0,
                titleWrapperSx: isProductDetailsPage ? {
                  marginBottom: 0
                } : void 0,
                titleVariant: isProductDetailsPage ? "t5" : void 0,
                ...titleProps
              }
            ),
            /* @__PURE__ */ jsx(
              CostcoBox,
              {
                sx: {
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "100%"
                },
                children: /* @__PURE__ */ jsx(CostcoBox, { sx: carouselContainerSx, children: /* @__PURE__ */ jsx(
                  Carousel,
                  {
                    containerSx: {
                      margin: 0,
                      padding: 0
                    },
                    items: ProductTiles({
                      analytics,
                      isLWResponseData,
                      carouselItems,
                      carouselTitle,
                      criteoDataSourceAttr,
                      dataPrivate,
                      disableBottomControls,
                      displayMultipleTiles,
                      finalProductTileSx,
                      hideReviewStarValue,
                      inventoryAware,
                      isHorizontal,
                      isProductDetailsPage,
                      locale,
                      maxProductTileImageHeight,
                      maxProductTileImageWidth,
                      minProductTileImageWidth,
                      pageType,
                      productData,
                      productTileContainerSx,
                      productTileMaxLineNumber,
                      productTileNumberSeparator,
                      product_tile_data_source,
                      showActionButton,
                      showReviews,
                      showSlideNumber,
                      variant,
                      isCriteo
                    }),
                    label: !ariaLabel && (titleProps == null ? void 0 : titleProps.title) ? null : ariaLabel,
                    labelledBy: titleProps == null ? void 0 : titleProps.titleId,
                    navigationButtonBottomOffset: isProductDetailsPage ? "auto" : navigationButtonBottomOffset,
                    navigationButtonTopOffset: isProductDetailsPage ? navigationButtonTopOffset : "auto",
                    navigationButtonsBottomAlign,
                    navigationButtonsMobileDisplay,
                    wrapAround: typeof wrapAround === "boolean" ? wrapAround : itemsPerSlide < carouselItems.length,
                    disableBottomControls,
                    itemsPerSlide,
                    peekAdjacent,
                    randomizeItems,
                    slideGap
                  }
                ) })
              }
            )
          ]
        }
      )
    }
  );
}
export {
  ProductTileCarouselUI
};
